import { useMemo, useState } from 'react';
import { Trophy, Swords, Plus, Download, Trash2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useMyMatches, computeRecord, useDeleteMatch, type MatchWithProfiles } from '@/hooks/useMatches';
import { MatchLogSheet } from '@/components/MatchLogSheet';
import { toast } from 'sonner';

export function MatchHistoryCard() {
  const { user } = useAuth();
  const { data: matches } = useMyMatches();
  const del = useDeleteMatch();
  const [showLog, setShowLog] = useState(false);
  const [filter, setFilter] = useState<'all' | 'event' | 'casual'>('all');

  const filtered = useMemo(() => {
    const list = matches || [];
    if (filter === 'event') return list.filter(m => !!m.event_id);
    if (filter === 'casual') return list.filter(m => !m.event_id);
    return list;
  }, [matches, filter]);

  const record = computeRecord(matches, user?.id);

  const downloadCsv = () => {
    const list = matches || [];
    if (!list.length) {
      toast.error('No matches to export');
      return;
    }
    const header = ['Date', 'Event', 'Opponent', 'Result', 'My Score', 'Opp Score', 'Notes'];
    const rows = list.map(m => {
      const isP1 = m.player1_id === user?.id;
      const opp = isP1 ? (m.player2_username || 'Opponent') : (m.player1_username || 'Opponent');
      const myScore = isP1 ? m.player1_score : m.player2_score;
      const oppScore = isP1 ? m.player2_score : m.player1_score;
      const result = m.winner_id === null
        ? (m.player1_score != null && m.player1_score === m.player2_score ? 'Draw' : '—')
        : m.winner_id === user?.id ? 'Win' : 'Loss';
      return [
        new Date(m.played_at).toISOString(),
        m.event_name || '',
        opp,
        result,
        myScore ?? '',
        oppScore ?? '',
        (m.notes || '').replace(/\n/g, ' '),
      ];
    });
    const csv = [header, ...rows]
      .map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `match-history-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Match history downloaded!');
  };

  if (!user) return null;

  return (
    <div className="mt-3 rounded-xl border border-border bg-card p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-medium text-foreground font-body flex items-center gap-1.5">
          <Swords className="h-3.5 w-3.5 text-primary" /> Match Record
        </h3>
        <div className="flex items-center gap-1">
          <button onClick={downloadCsv} aria-label="Download CSV" className="rounded-md p-1.5 text-muted-foreground hover:bg-secondary hover:text-primary transition-colors">
            <Download className="h-3.5 w-3.5" />
          </button>
          <button onClick={() => setShowLog(true)} className="flex items-center gap-1 rounded-md bg-primary/10 px-2 py-1 text-[10px] font-medium text-primary font-body hover:bg-primary/20 transition-colors">
            <Plus className="h-3 w-3" /> Log Match
          </button>
        </div>
      </div>

      {/* Record */}
      <div className="grid grid-cols-4 gap-2 mb-3">
        <Stat label="Wins" value={record.wins} accent="ink-emerald" />
        <Stat label="Losses" value={record.losses} accent="ink-ruby" />
        <Stat label="Draws" value={record.draws} />
        <Stat label="Win %" value={`${record.winRate}%`} accent="primary" />
      </div>

      {/* Filter */}
      {(matches?.length || 0) > 0 && (
        <div className="flex items-center gap-1 rounded-lg bg-secondary p-0.5 mb-2">
          {(['all', 'event', 'casual'] as const).map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`flex-1 rounded-md py-1 text-[10px] font-medium font-body capitalize transition-colors ${
                filter === f ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      )}

      {/* Recent list */}
      {!matches?.length ? (
        <p className="py-6 text-center text-[11px] text-muted-foreground font-body">
          No matches yet. Tap "Log Match" after your next game!
        </p>
      ) : !filtered.length ? (
        <p className="py-4 text-center text-[11px] text-muted-foreground font-body">No matches in this view</p>
      ) : (
        <ul className="space-y-1.5 max-h-64 overflow-y-auto">
          {filtered.slice(0, 30).map(m => (
            <MatchRow
              key={m.id}
              match={m}
              userId={user.id}
              onDelete={async () => {
                if (!confirm('Delete this match?')) return;
                try { await del.mutateAsync(m.id); toast.success('Match deleted'); }
                catch (err: any) { toast.error(err.message); }
              }}
            />
          ))}
        </ul>
      )}

      <MatchLogSheet open={showLog} onClose={() => setShowLog(false)} />
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: number | string; accent?: string }) {
  const colorClass = accent === 'ink-emerald' ? 'text-ink-emerald'
    : accent === 'ink-ruby' ? 'text-ink-ruby'
    : accent === 'primary' ? 'text-primary'
    : 'text-foreground';
  return (
    <div className="rounded-lg border border-border bg-background/40 p-2 text-center">
      <p className={`font-display text-base font-bold ${colorClass}`}>{value}</p>
      <p className="text-[9px] text-muted-foreground font-body uppercase tracking-wider">{label}</p>
    </div>
  );
}

function MatchRow({ match, userId, onDelete }: { match: MatchWithProfiles; userId: string; onDelete: () => void }) {
  const isP1 = match.player1_id === userId;
  const oppName = isP1 ? (match.player2_username || 'Opponent') : (match.player1_username || 'Opponent');
  const result = match.winner_id === null
    ? (match.player1_score != null && match.player1_score === match.player2_score ? 'D' : '—')
    : match.winner_id === userId ? 'W' : 'L';
  const tone = result === 'W' ? 'bg-ink-emerald/15 text-ink-emerald'
    : result === 'L' ? 'bg-ink-ruby/15 text-ink-ruby'
    : 'bg-secondary text-muted-foreground';
  const myScore = isP1 ? match.player1_score : match.player2_score;
  const oppScore = isP1 ? match.player2_score : match.player1_score;
  return (
    <li className="flex items-center gap-2 rounded-lg border border-border bg-background/40 px-2.5 py-1.5">
      <span className={`flex h-6 w-6 items-center justify-center rounded-md text-[10px] font-bold font-body ${tone}`}>{result}</span>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] text-foreground font-body truncate">vs {oppName}</p>
        <p className="text-[9px] text-muted-foreground font-body truncate">
          {match.event_name ? `${match.event_name} · ` : ''}
          {new Date(match.played_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
        </p>
      </div>
      {(myScore != null || oppScore != null) && (
        <span className="text-[10px] text-muted-foreground font-body tabular-nums">
          {myScore ?? '·'}–{oppScore ?? '·'}
        </span>
      )}
      <button onClick={onDelete} aria-label="Delete match" className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-ink-ruby transition-colors">
        <Trash2 className="h-3 w-3" />
      </button>
    </li>
  );
}
